import React, { useState, useEffect } from "react";
import { Steps } from "intro.js-react";
import 'intro.js/introjs.css';
import "./attendee.css";
import AttendeeMeetingView from "../../../components/mediaplayer/attendee-meeting-sdk";
import PresentationViewer from "../../../components/slider/attendee-slider/ppt-view";
import SpeakerBio from "../../../components/speakerbio/speaker-bio";
import RelatedContent from "../../../components/speakerbio/related-content";
import { useParams } from "react-router-dom";
import { EventFetchSlides } from "../../../services/event/attendee-event-slides";
import Loader from "../../../utils/loader/loader"; // Import the Loader component
import { fetchPollAllResults } from "../../../services/event/pollAllResults";

interface SdkData {
  hideQna?: boolean | undefined;
  meetingNumber: string;
  password: string;
  userName: string;
  userEmail: string;
  signature: string;
  tk?: string;
}

interface Question {
  pollId: string;
  questionText: string;
  options: Record<string, string>;
  sequence: number;
}

interface PollResult {
  option: string;
  percentage: number;
  voteCount: number;
}

interface PollResultData {
  questionTitle: string;
  totalVotes: number;
  results: PollResult[];
}

const AttendeeDashboardView: React.FC<{ sdkData: SdkData; eventData: any }> = ({
  sdkData,
  eventData,
}) => {
  const { eventId } = useParams();
  const [slides, setSlides] = useState<any[]>([]);
  const [speakerBio, setSpeakerBio] = useState<any[]>([]);
  const [relatedContent, setRelatedContent] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pollQuestions, setPollQuestions] = useState<Question[]>([]);
  const [allPollResults, setAllPollResults] = useState<PollResultData[]>([]);
  const [defaultSpeakers, setDefaultSpeakers] = useState<any[]>([]);
  const [meetingHeight, setMeetingHeight] = useState(0);
  const [meetingSdkViewFlag, setMeetingSdkViewFlag] = useState<any>([]);
  const [isSlides, setIsSlides] = useState<any>([]);
  const [isRelatedContent, setIsRelatedContent] = useState<any>([]);
  const [startMeeting, setStartMeeting] = useState<any>(false);
  const [stepsEnabled, setStepsEnabled] = useState(false);
  const [responsiveMeetingHeight, setResponsiveMeetingHeight] = useState(0);
  useEffect(() => {
  }, [isLoading]);


  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await EventFetchSlides(Number(eventId));
        setSlides(response);
        setSpeakerBio(response?.collaterals?.speakerbio);
        setRelatedContent(response?.collaterals?.attachments);
        setPollQuestions(response.collaterals.pollQuestions);
        setDefaultSpeakers(response.defaultSpeakers);
        // response.collaterals.slides = [];
        // If slides data is empty, set custom; otherwise set default
        if (response?.collaterals?.slides.length === 0) {
          setMeetingSdkViewFlag('custom');
          setIsSlides(false);
          setStartMeeting(true);
        } else {
          setMeetingSdkViewFlag('default');
          setIsSlides(true);
          setStartMeeting(true);
        }

        if (response?.collaterals?.attachments.length === 0) {
          setIsRelatedContent(false);
        }else{
          setIsRelatedContent(true);
        }
      } catch (error) {
        console.error("Error fetching slides:", error);
      }
    };

    fetchData();
  }, [eventId]);

  useEffect(() => {
    const fetchPollResult = async () => {
      try {
        const response = await fetchPollAllResults(Number(eventId));
        setAllPollResults(response);
      } catch (error) {
        console.error("Error fetching poll results:", error);
      }
    };

    fetchPollResult();
  }, [eventId]);

  // Trigger the steps when loading is done
  useEffect(() => {
    if (!isLoading) {
      setStepsEnabled(true);
    }
  }, [isLoading]);

  const getHeightOfMeeting = (height: number) => {
    setMeetingHeight(height);
  };

  const getHeightOfMeetingResponsive = (height: number) => {
    setResponsiveMeetingHeight(height);
  };
  
  const allSteps = [
    {
      element: '[aria-label="Zoom Web SDK Widget"]',
      intro: 'When the session begins, on-camera speakers will appear here.',
      scrollToElement: true,
      position: 'bottom',
    },
    {
      element: 'button[aria-label="Join Audio"]',
      intro: 'Click this icon to turn on the session\'s audio and choose your speaker output.',
      scrollToElement: true,
    },
    {
      element: '[aria-label="Chat"].attendee-chat-style',
      intro: 'Message the hosts and panelists directly with questions or comments here.',
      scrollToElement: true,
    },
    {
      element: '#presentationViewer', 
      intro: 'This is the presentation viewer. You can view the slides and answer poll questions here.',
      scrollToElement: true,
      condition: 'showSlides', 
    },
    {
      element: '#relatedContent',
      intro: 'Download related content and additional resources here.',
      scrollToElement: true,
      condition: 'showRelatedContent',
    },
    {
      element: '#bio-speaker-view',
      intro: 'You can find speaker information here.',
      scrollToElement: true,
    },
    {
      element: '[aria-label="Q&A"].zoom-MuiPopper-root', 
      intro: 'You can engage with the panelists in group discussions here.',
      scrollToElement: true,
      position: 'top',
      condition: 'showQnA', 
    },
    
  ];

  const filteredSteps = allSteps.filter(step => {
    if (step.condition === 'showQnA') {
      return !sdkData?.hideQna; 
    }
    if (step.condition === 'showSlides') {
      return isSlides; 
    }
    if (step.condition === 'showRelatedContent') {
      return isRelatedContent; 
    }
    return true; 
  });
  
  const stepShownHandler = () => {
    setTimeout(() => {
      const skipBtn = document.querySelector('.introjs-skipbutton')
      const targetDiv = document.querySelector('.introjs-tooltipbuttons')
      
      if(skipBtn && targetDiv) {
        targetDiv.appendChild(skipBtn)
      }
    }, 100);
  }

  return (
    <div className="attendee-main-panel">
      {isLoading && <Loader />}
      <Steps
        onStart={stepShownHandler}
        enabled={stepsEnabled}
        steps={filteredSteps}
        initialStep={0}
        onExit={() => setStepsEnabled(false)}
        options={{
          showButtons: true,
          exitOnOverlayClick: false,
          nextLabel: 'Next',
          skipLabel: "Skip all",
          prevLabel: 'Back',
          doneLabel: 'Finish',
          disableInteraction: false,
        }}
      />
      <div className="attendee-header">
        <div className="event-title-attendee">{eventData?.title}</div>
      </div>
      <div className="main-inner-wrapper">
        <div className="inner-left-side">
          <div className="attendee-meeting-wrapper" id="attendeeMeetingView">
            {startMeeting && (
              <AttendeeMeetingView
                meetingNumber={sdkData.meetingNumber}
                password={sdkData.password}
                userName={sdkData.userName}
                userEmail={sdkData.userEmail}
                leaveUrl="https://example.com"
                signature={sdkData.signature}
                tk={sdkData.tk}
                setLoading={setIsLoading}
                meetingSdkViewFlag={meetingSdkViewFlag}
                getHeightOfMeeting={getHeightOfMeeting}
                hideQna={sdkData?.hideQna}
                getHeightOfMeetingResponsive={getHeightOfMeetingResponsive}
              />
            )}
            <div
              className="attendee-presentationview-wrapper"
              style={{
                marginTop: responsiveMeetingHeight
                  ? responsiveMeetingHeight + 20
                  : undefined,
              }}
            >
              {isSlides && (
                <div id="presentationViewer">
                  <PresentationViewer
                    slidesData={slides}
                    pollData={pollQuestions}
                    allPollResultData={allPollResults}
                    email={sdkData.userEmail}
                  />
                </div>
              )}
              {!sdkData?.hideQna && isRelatedContent && (
                <div
                className={`related-content-wrapper ${meetingSdkViewFlag === 'custom' && meetingHeight && 'custom-related-content'}`}
                  id="relatedContent"
                  style={{
                  marginTop: meetingSdkViewFlag === 'custom' && meetingHeight ? meetingHeight + 10 : undefined,
                  left: meetingSdkViewFlag === 'custom' && meetingHeight ? '450px' : undefined,
                  width: meetingSdkViewFlag === 'custom' && meetingHeight ? 'calc(100vw - 760px)' : undefined,
                  }}
                >
                  <RelatedContent relatedContent={relatedContent} />
              </div> )};
            </div>
          </div>
        </div>
        {!isLoading && (
          <div className="inner-right-side">
            <div className="attendee-chat-wrapper" id="attendeeattndeeChatElementwrapper">
              <div id="attendeeattndeeChatElement" className="chat-panel"></div>
            </div>
            <div className="attendee-speaker-bio-wrapper" id="bio-speaker">
              <SpeakerBio speakerBio={speakerBio} defaultSpeakers={defaultSpeakers} />
            </div>
            { sdkData?.hideQna && isRelatedContent && (
              <div
                className={`related-content-wrapper custom-related-content-qa ${meetingSdkViewFlag === 'custom' && meetingHeight && 'custom-related-content-qa'}`}
                id="relatedContent"
                style={{
                  marginTop: meetingSdkViewFlag === 'custom' && meetingHeight ? meetingHeight + 10 : undefined,
                  left: meetingSdkViewFlag === 'custom' && meetingHeight ? '450px' : undefined,
                  width: meetingSdkViewFlag === 'custom' && meetingHeight ? 'calc(100vw - 760px)' : undefined,
                }}
              >
                <RelatedContent relatedContent={relatedContent} />
              </div> )};
          </div>
        )}
      </div>
    </div >
  );
};

export default AttendeeDashboardView;
